import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  LuCheck,
  LuMail,
  LuMapPin,
  LuSend,
  LuShieldCheck,
  LuUserRound,
} from "react-icons/lu";
import {
  accessExtractMessage,
  createAccessUser,
  fetchAccessRoleById,
  fetchAccessRoles,
  fetchAccessUserById,
  updateAccessUser,
} from "../../services/superAdminAccess.service";
import { useLocationMaster } from "../../hooks/useLocationMaster";
import "../../assets/styles/super_admin_access.scss";

const SectionHeading = ({ number, title, description, icon: Icon }) => (
  <div className="sa-user-form__section-head">
    <div className="sa-user-form__section-copy">
      <span className="sa-user-form__step">{number}</span>
      <span>
        <strong>{title}</strong>
        <small>{description}</small>
      </span>
    </div>
    <Icon aria-hidden />
  </div>
);

const getRolePermissionSummary = (role) => {
  const resources = Object.entries(role?.permissions || {}).filter(
    ([, actions]) => Object.values(actions || {}).some(Boolean),
  );

  return {
    modules: new Set(resources.map(([key]) => key.split(".")[0])).size,
    permissions: resources.reduce(
      (total, [, actions]) =>
        total + Object.values(actions || {}).filter(Boolean).length,
      0,
    ),
  };
};

const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  mobile: "",
  company: "",
  department: "",
  designation: "",
  address: "",
  countryId: "",
  stateId: "",
  cityId: "",
  pincode: "",
  roleId: "",
  status: "Active",
  sendInvite: true,
};

export default function SuperAdminCreateUserPage({ basePath = "/super-admin" }) {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [searchParams] = useSearchParams();
  const isEditMode = Boolean(userId);
  const isSupportManager = basePath.includes("/support-manager");
  const isLogisticsManager = basePath.includes("/logistics-manager");

  const usersListLabel = isLogisticsManager
    ? "Logistics Users"
    : isSupportManager
      ? "Support Users"
      : "User Management";
  const pageTitle = isEditMode
    ? isLogisticsManager
      ? "Edit Logistics User"
      : "Edit User"
    : isLogisticsManager
      ? "Create Logistics User"
      : "Create User";
  const pageLead = isEditMode
    ? isLogisticsManager
      ? "Update logistics team member details and portal access."
      : isSupportManager
        ? "Update support team member details and portal access."
        : "Update platform user details and access permissions."
    : isLogisticsManager
      ? "Add a logistics team member and configure role-based access."
      : isSupportManager
        ? "Add a support team member and configure role-based access."
        : "Add a new platform user and configure access permissions.";

  const [form, setForm] = useState(EMPTY_FORM);
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [loading, setLoading] = useState(isEditMode);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [saving, setSaving] = useState(false);
  const {
    countries,
    states,
    cities,
    loading: loadingCountries,
    loadingStates,
    loadingCities,
    updateListsForCountry,
    updateListsForState,
  } = useLocationMaster();

  const labelForId = (items, id) =>
    items.find((item) => String(item.id) === String(id))?.label || "";

  const handleCountryChange = (countryId) => {
    setForm((current) => ({
      ...current,
      countryId,
      stateId: "",
      cityId: "",
    }));
    void updateListsForCountry(countryId || null);
  };

  const handleStateChange = (stateId) => {
    setForm((current) => ({ ...current, stateId, cityId: "" }));
    void updateListsForState(form.countryId, stateId || null);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingRoles(true);
      try {
        const data = await fetchAccessRoles({ status: "Active" });
        if (!cancelled) {
          setRoles(Array.isArray(data.items) ? data.items : []);
        }
      } catch (error) {
        if (!cancelled) {
          toast.error(accessExtractMessage(error, "Failed to load roles."));
          setRoles([]);
        }
      } finally {
        if (!cancelled) setLoadingRoles(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (isEditMode || form.roleId || !roles.length) return;

    const requestedRoleLevel = searchParams.get("roleLevel");
    const defaultRoleLevel = isLogisticsManager ? "logistics_user" : requestedRoleLevel;
    if (!defaultRoleLevel) return;

    const matchedRole = roles.find(
      (role) =>
        role.roleLevel === defaultRoleLevel ||
        role.role_level === defaultRoleLevel,
    );
    if (!matchedRole) return;

    setForm((current) => ({ ...current, roleId: String(matchedRole.id) }));
  }, [isEditMode, form.roleId, isLogisticsManager, roles, searchParams]);

  useEffect(() => {
    if (!form.roleId) {
      setSelectedRole(null);
      return undefined;
    }

    let cancelled = false;
    fetchAccessRoleById(form.roleId)
      .then((role) => {
        if (!cancelled) setSelectedRole(role);
      })
      .catch(() => {
        if (!cancelled) setSelectedRole(null);
      });

    return () => {
      cancelled = true;
    };
  }, [form.roleId]);

  useEffect(() => {
    if (!isEditMode) return undefined;

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const user = await fetchAccessUserById(userId);
        if (cancelled) return;
        if (!user) {
          toast.error("User not found.");
          navigate(`${basePath}/users`);
          return;
        }
        setForm({
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          email: user.email || "",
          mobile: user.mobile || "",
          company: user.company || "",
          department: user.department || "",
          designation: user.designation || "",
          address: user.address || "",
          countryId: String(user.countryId || ""),
          stateId: String(user.stateId || ""),
          cityId: String(user.cityId || ""),
          pincode: user.pincode || "",
          roleId: String(user.roleId || ""),
          status: user.status || "Active",
          sendInvite: false,
        });
        if (user.countryId) {
          await updateListsForCountry(user.countryId);
        }
        if (user.stateId) {
          await updateListsForState(user.countryId, user.stateId);
        }
      } catch (error) {
        if (!cancelled) {
          toast.error(accessExtractMessage(error, "Failed to load user."));
          navigate(`${basePath}/users`);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    basePath,
    isEditMode,
    navigate,
    updateListsForCountry,
    updateListsForState,
    userId,
  ]);

  const onChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const onSubmit = async (event, invite = false) => {
    event.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim()) {
      toast.error("Please enter first and last name.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!form.mobile.trim()) {
      toast.error("Please enter a mobile number.");
      return;
    }
    if (!form.company) {
      toast.error("Please enter a company name.");
      return;
    }
    if (!form.countryId) {
      toast.error("Please select a country.");
      return;
    }
    if (!form.stateId) {
      toast.error("Please select a state.");
      return;
    }
    if (!form.cityId) {
      toast.error("Please select a city.");
      return;
    }
    if (!form.roleId) {
      toast.error("Please assign a role.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        mobile: form.mobile.trim(),
        company: form.company,
        department: form.department.trim(),
        designation: form.designation.trim(),
        address: form.address.trim(),
        countryId: Number(form.countryId),
        country: labelForId(countries, form.countryId),
        stateId: Number(form.stateId),
        state: labelForId(states, form.stateId),
        cityId: Number(form.cityId),
        city: labelForId(cities, form.cityId),
        pincode: form.pincode.trim(),
        roleId: Number(form.roleId),
        status: invite || form.sendInvite ? "Pending" : form.status,
        sendInvite: invite || form.sendInvite,
      };

      if (isEditMode) {
        await updateAccessUser(userId, {
          ...payload,
          status: form.status,
          // Only invite when explicitly clicking Update & Invite
          sendInvite: invite ? true : undefined,
        });
        toast.success(
          invite
            ? "User updated and invitation email sent."
            : "User updated successfully.",
        );
      } else {
        await createAccessUser(payload);
        toast.success(
          invite || form.sendInvite
            ? "User saved and invitation email sent."
            : "User saved successfully.",
        );
      }
      navigate(`${basePath}/users`);
    } catch (error) {
      toast.error(
        accessExtractMessage(
          error,
          isEditMode ? "Failed to update user." : "Failed to save user.",
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  const roleSummary = getRolePermissionSummary(selectedRole);

  if (loading) {
    return (
      <div className="sa-access-page">
        <div className="sa-access-empty">Loading user...</div>
      </div>
    );
  }

  return (
    <div className="sa-access-page">
      <nav className="sa-access-breadcrumb" aria-label="Breadcrumb">
        <Link to={`${basePath}/users`}>{usersListLabel}</Link>
        <span aria-hidden>&gt;</span>
        <span aria-current="page">{pageTitle}</span>
      </nav>

      <header className="sa-access-head">
        <div>
          <h1>{pageTitle}</h1>
          <p>{pageLead}</p>
        </div>
        <div className="sa-access-actions">
          <button
            type="button"
            className="sa-access-btn sa-access-btn--ghost"
            onClick={() => navigate(`${basePath}/users`)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="sa-access-btn sa-access-btn--primary"
            disabled={saving || loadingRoles}
            onClick={(event) => onSubmit(event, true)}
          >
            {saving ? "Saving..." : isEditMode ? "Update & Invite" : "Save & Invite"}
          </button>
        </div>
      </header>

      <form
        className={`sa-user-form${isLogisticsManager ? " sa-user-form--pill-inputs" : ""}`}
        onSubmit={(event) => onSubmit(event, false)}
      >
        <section className="sa-access-section sa-user-form__section">
          <SectionHeading
            number="1"
            title="Basic Information"
            description="Provide the basic details of the user."
            icon={LuUserRound}
          />
          <div className="sa-access-grid sa-access-grid--four">
            <label className="sa-access-field">
              <span>
                First Name <em>*</em>
              </span>
              <input
                name="firstName"
                value={form.firstName}
                onChange={onChange}
                placeholder="Enter first name"
                required
              />
            </label>
            <label className="sa-access-field">
              <span>
                Last Name <em>*</em>
              </span>
              <input
                name="lastName"
                value={form.lastName}
                onChange={onChange}
                placeholder="Enter last name"
                required
              />
            </label>
            <label className="sa-access-field">
              <span>
                Email Address <em>*</em>
              </span>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={onChange}
                placeholder="Enter email address"
                required
              />
            </label>
            <label className="sa-access-field">
              <span>
                Mobile Number <em>*</em>
              </span>
              <input
                name="mobile"
                value={form.mobile}
                onChange={onChange}
                placeholder="Enter mobile number"
                required
              />
            </label>
            <label className="sa-access-field">
              <span>
                Company Name <em>*</em>
              </span>
              <input
                name="company"
                value={form.company}
                onChange={onChange}
                placeholder="Enter company name"
                required
              />
            </label>
            <label className="sa-access-field">
              <span>Department</span>
              <input
                name="department"
                value={form.department}
                onChange={onChange}
                placeholder="Enter department"
              />
            </label>
            <label className="sa-access-field">
              <span>Designation</span>
              <input
                name="designation"
                value={form.designation}
                onChange={onChange}
                placeholder="Enter designation"
              />
            </label>
          </div>
        </section>

        <section className="sa-access-section sa-user-form__section">
          <SectionHeading
            number="2"
            title="Address"
            description="Provide the address details of the user."
            icon={LuMapPin}
          />
          <div className="sa-access-grid" style={{ marginBottom: 14 }}>
            <label className="sa-access-field">
              <span>Address (Optional)</span>
              <input
                name="address"
                value={form.address}
                onChange={onChange}
                placeholder="Enter address"
              />
            </label>
          </div>
          <div className="sa-access-grid sa-access-grid--four">
            <label className="sa-access-field">
              <span>
                Country <em>*</em>
              </span>
              <select
                value={form.countryId}
                onChange={(event) => handleCountryChange(event.target.value)}
                disabled={loadingCountries}
                required
              >
                <option value="">
                  {loadingCountries ? "Loading countries..." : "Select country"}
                </option>
                {countries.map((country) => (
                  <option key={country.id} value={country.id}>
                    {country.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="sa-access-field">
              <span>
                State <em>*</em>
              </span>
              <select
                value={form.stateId}
                onChange={(event) => handleStateChange(event.target.value)}
                disabled={!form.countryId || loadingStates}
                required
              >
                <option value="">
                  {!form.countryId
                    ? "Select country first"
                    : loadingStates
                      ? "Loading states..."
                      : "Select state"}
                </option>
                {states.map((state) => (
                  <option key={state.id} value={state.id}>
                    {state.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="sa-access-field">
              <span>
                City <em>*</em>
              </span>
              <select
                value={form.cityId}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    cityId: event.target.value,
                  }))
                }
                disabled={!form.stateId || loadingCities}
                required
              >
                <option value="">
                  {!form.stateId
                    ? "Select state first"
                    : loadingCities
                      ? "Loading cities..."
                      : "Select city"}
                </option>
                {cities.map((city) => (
                  <option key={city.id} value={city.id}>
                    {city.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="sa-access-field">
              <span>Pincode</span>
              <input
                name="pincode"
                value={form.pincode}
                onChange={onChange}
                placeholder="Enter pincode"
                maxLength={20}
              />
            </label>
          </div>
        </section>

        <section className="sa-access-section sa-user-form__section">
          <SectionHeading
            number="3"
            title="Role & Access"
            description="Assign role and set user status."
            icon={LuShieldCheck}
          />
          <div className="sa-user-form__role-grid">
            <label className="sa-access-field">
              <span>
                Role <em>*</em>
              </span>
              <select
                name="roleId"
                value={form.roleId}
                onChange={onChange}
                required
                disabled={loadingRoles}
              >
                <option value="">
                  {loadingRoles ? "Loading roles..." : "Select role"}
                </option>
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="sa-access-field">
              <span>
                Status <em>*</em>
              </span>
              <select name="status" value={form.status} onChange={onChange}>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                {isEditMode ? <option value="Pending">Pending</option> : null}
              </select>
            </label>
            <div className="sa-user-form__role-summary">
              <span className="sa-user-form__role-icon">
                <LuShieldCheck aria-hidden />
              </span>
              <p>
                This role will provide access to
                <strong>
                  {roleSummary.modules} Module
                  {roleSummary.modules === 1 ? "" : "s"}
                </strong>
                <span>
                  and {roleSummary.permissions} Permission
                  {roleSummary.permissions === 1 ? "" : "s"}
                </span>
              </p>
            </div>
          </div>
        </section>

        <section className="sa-access-section sa-user-form__section sa-user-form__invitation">
          <div>
            <SectionHeading
              number="4"
              title="Invitation"
              description="Send an invitation email to the user."
              icon={LuSend}
            />
            <label className="sa-user-form__invite-check">
              <input
                type="checkbox"
                name="sendInvite"
                checked={form.sendInvite}
                onChange={onChange}
              />
              <span>
                <strong>Send Invitation Email</strong>
                <p>
                  An invitation email with a secure link and temporary password
                  will be sent to the user.
                </p>
              </span>
            </label>
          </div>
          <div className="sa-user-form__mail-art" aria-hidden>
            <span className="sa-user-form__mail-check">
              <LuCheck />
            </span>
            <LuMail />
            <LuSend />
          </div>
        </section>

        <div className="sa-access-form-footer">
          <button
            type="button"
            className="sa-access-btn sa-access-btn--ghost"
            onClick={() => navigate(`${basePath}/users`)}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="sa-access-btn sa-access-btn--ghost"
            disabled={saving || loadingRoles}
          >
            {saving ? "Saving..." : isEditMode ? "Update" : "Save"}
          </button>
          <button
            type="button"
            className="sa-access-btn sa-access-btn--primary"
            disabled={saving || loadingRoles}
            onClick={(event) => onSubmit(event, true)}
          >
            {saving
              ? "Saving..."
              : isEditMode
                ? "Update & Invite"
                : "Save & Invite"}
          </button>
        </div>
      </form>
    </div>
  );
}
